import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { clearOnboardingVisitedSession } from '@/hooks/use-onboarding';
import { api } from '@/lib/api';
import { getSafeRedirectPath } from '@/lib/url-utils';
import {
  type BctTheme,
  type NotificationPreference,
  type UserRole,
  useAuthStore,
} from '@/stores/auth';

export function AuthCallback() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setAccessToken, setLoading, setLoginContext } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  // Use a ref for `t` so the callback effect doesn't re-fire when the language changes.
  // The ref is updated in a separate effect to satisfy the React compiler lint rule
  // that forbids writing to refs during render.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    async function handleCallback() {
      // Check for error in URL params (OAuth error)
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription ?? tRef.current('callback.authFailed'));
        setLoading(false);
        return;
      }

      // Get one-time authorization code from URL (NOT the token)
      // The actual token is exchanged securely via POST to prevent URL exposure
      const authCode = searchParams.get('code');

      if (authCode) {
        try {
          // Exchange the one-time code for an access token via secure POST
          // This prevents token exposure in browser history, server logs, and referrer headers
          const exchangeResponse = await api.post<{ accessToken: string }>('/auth/exchange', {
            code: authCode,
          });

          if (!exchangeResponse.data?.accessToken) {
            setError(tRef.current('callback.invalidCode'));
            setLoading(false);
            return;
          }

          setAccessToken(exchangeResponse.data.accessToken);

          // Fetch current user data
          // NOTE: This endpoint returns the authenticated user's own data (including PII).
          // This is acceptable as users can view their own profile. The backend should
          // ensure this endpoint only returns the authenticated user's data, not others'.
          const response = await api.get<{
            id: string;
            email: string;
            name: string;
            role: UserRole;
            tenantId: string | null;
            profilePictureUrl: string | null;
            authProvider: 'EMAIL' | 'MICROSOFT' | 'GOOGLE';
            isActive: boolean;
            preferredLanguage?: string;
            notificationPreference?: NotificationPreference;
            // Contact & Availability fields
            phoneNumber?: string | null;
            phoneVisible?: boolean;
            weeklyAttendance?: Record<string, string | null> | null;
            attendanceVisible?: boolean;
            organizationTheme?: BctTheme;
          }>('/auth/me');

          if (response.data) {
            setUser(response.data);
            setLoginContext('fresh');
            // Clear session tour tracking for fresh login - tours will show on first visit to each page
            clearOnboardingVisitedSession();
            // PII RETENTION: User data stored in auth store (memory) is cleared on logout.
            // No PII is persisted to localStorage/sessionStorage here.
          }

          // Get redirect destination from session storage (non-PII data only)
          // Validate the redirect path to prevent open redirect vulnerabilities
          const storedPath = sessionStorage.getItem('auth:redirect');
          sessionStorage.removeItem('auth:redirect');
          const redirectPath = getSafeRedirectPath(storedPath, '/dashboard');

          // Navigate to intended destination
          navigate(redirectPath, { replace: true });
        } catch {
          setError(tRef.current('callback.completionFailed'));
          setLoading(false);
        }
      } else {
        // No code - the OAuth flow might have failed silently
        // or this page was accessed directly
        setError(tRef.current('callback.noCode'));
        setLoading(false);
      }
    }

    handleCallback();
  }, [searchParams, setAccessToken, setUser, setLoading, setLoginContext, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold">{t('callback.errorTitle')}</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
              {t('callback.returnToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{t('callback.completingSignIn')}</p>
      </div>
    </div>
  );
}
